import { ColumnText } from '@ddt-web/data-service';
import { ColDef, ColGroupDef } from 'ag-grid-community';

import { FundAnalysisColumnSortingUtil } from '../util/fund-analysis-column-sorting.util';
import { FULL_WIDTH, HEAVY_BORDER_LEFT, SOFT_BORDER_LEFT, SOFT_DASHED_BORDER_LEFT } from './cell-classes';

/**
 * This class contains all the static column definitions for the Investment Details table and the pdf FundHealthStatusAnalysis component
 * The dimension columns are all dynamically generated in the FundAnalysisColumnTransformerService
 * Please check the correspondent pdf component anytime any changes are made here
 */
const simpleNumberDefaultColDef: Partial<ColDef> = {
	headerClass: [SOFT_BORDER_LEFT],
	cellRenderer: 'numberCellComponent',
	headerComponentParams: {
		isRightAligned: true
	}
};

export const hiddenColumnDefaultColDef: Partial<ColDef> = {
	...simpleNumberDefaultColDef,
	headerClass: [SOFT_DASHED_BORDER_LEFT],
	columnGroupShow: 'open',
	sortable: true,
	comparator: FundAnalysisColumnSortingUtil.defaultColumnComparator
};

export const commonLeftColumnDefs: Array<ColDef> = [
	{
		field: 'status',
		pinned: 'left',
		cellRenderer: 'statusCellComponent',
		headerComponentParams: {
			headerText: new ColumnText('FUND_ANALYSIS_STATUS', 'Status'),
			headerTooltip: new ColumnText('FUND_ANALYSIS_STATUS_TOOLTIP')
		},
		cellRendererParams: {
			cellClass: [FULL_WIDTH, 'padding-4'],
			enableTooltip: true
		},
		width: 65,
		valueGetter: params => params.data.isMorningstarCategory ? params.data.category : params.data.status,
		colSpan: params => params.data.isMorningstarCategory ? 4 : 1,
		sort: 'asc', // Only one sort type is supported for the status column
		sortIndex: 0, // First sort within the morningstar category is status
		comparator: FundAnalysisColumnSortingUtil.statusColumnComparator
	},
	{
		field: 'ticker',
		pinned: 'left',
		sortable: true,
		width: 72,
		headerComponentParams: {
			headerText: new ColumnText('FUND_ANALYSIS_TICKER', 'Ticker'),
			headerTooltip: new ColumnText('FUND_ANALYSIS_TICKER_TOOLTIP')
		},
		sort: 'asc',
		sortIndex: 2, // third sort within the morningstar category is ticker (second comes from FundAnalysisColumnTransformerService)
		comparator: FundAnalysisColumnSortingUtil.defaultColumnComparator
	},
	{
		field: 'description',
		pinned: 'left',
		sortable: true,
		width: 200,
		cellRenderer: 'fundNameCellComponent',
		comparator: FundAnalysisColumnSortingUtil.defaultColumnComparator,
		headerComponentParams: {
			headerText: new ColumnText('FUND_ANALYSIS_DESCRIPTION', 'Name'),
			headerTooltip: new ColumnText('FUND_ANALYSIS_DESCRIPTION_TOOLTIP')
		}
	}
];

const webCommonLeftPinnedColumnDefs: Array<ColDef> = [
	{
		field: 'checkbox',
		headerName: '',
		pinned: 'left',
		headerCheckboxSelection: true,
		headerCheckboxSelectionFilteredOnly: true,
		checkboxSelection: params => !params.data.isMorningstarCategory,
		width: 30,
		hide: true
	},
	...commonLeftColumnDefs
];

const tagColumn: ColDef = {
	field: 'tags',
	pinned: 'left',
	width: 106,
	headerClass: [SOFT_BORDER_LEFT],
	cellRenderer: 'fundTagCellComponent',
	headerComponentParams: {
		headerText: new ColumnText('FUND_ANALYSIS_TAGS', 'Tags'),
		headerTooltip: new ColumnText('FUND_ANALYSIS_TAGS_TOOLTIP')
	},
	cellRendererParams: {
		cellClass: [SOFT_BORDER_LEFT, 'padding-4', 'full-size']
	}
};

export const statusViewLeftPinnedColumnDefs: Array<ColGroupDef | ColDef> = [
	...webCommonLeftPinnedColumnDefs,
	{
		headerName: '',
		headerClass: [SOFT_BORDER_LEFT],
		children: [tagColumn]
	}
];

export const aofViewLeftPinnedColumnDefs: Array<ColGroupDef | ColDef> = [
	...webCommonLeftPinnedColumnDefs,
	tagColumn
];

export const statusViewPortfolioColumns: Array<ColGroupDef> = [
	{
		headerName: '',
		headerClass: [HEAVY_BORDER_LEFT],
		children: [{
			field: 'weight',
			sortable: true,
			...simpleNumberDefaultColDef,
			headerClass: [HEAVY_BORDER_LEFT],
			cellRendererParams: {
				cellClass: [HEAVY_BORDER_LEFT]
			},
			headerComponentParams: {
				...simpleNumberDefaultColDef.headerComponentParams,
				headerText: new ColumnText('FUND_ANALYSIS_WEIGHT', 'Weight'),
				headerTooltip: new ColumnText('FUND_ANALYSIS_WEIGHT_TOOLTIP')
			},
			comparator: FundAnalysisColumnSortingUtil.defaultColumnComparator
		}]
	},
	{
		headerName: '',
		headerClass: [SOFT_BORDER_LEFT],
		children: [{
			field: 'aum',
			headerClass: [SOFT_BORDER_LEFT],
			sortable: true,
			...simpleNumberDefaultColDef,
			cellRendererParams: {
				cellClass: [SOFT_BORDER_LEFT]
			},
			headerComponentParams: {
				...simpleNumberDefaultColDef.headerComponentParams,
				headerText: new ColumnText('FUND_ANALYSIS_AUM', 'AUM'),
				headerTooltip: new ColumnText('FUND_ANALYSIS_AUM_TOOLTIP')
			},
			comparator: FundAnalysisColumnSortingUtil.defaultColumnComparator
		}]
	},
	{
		headerName: '',
		headerClass: [SOFT_BORDER_LEFT],
		children: [{
			field: 'portfolios',
			sortable: true,
			...simpleNumberDefaultColDef,
			headerClass: [SOFT_BORDER_LEFT],
			cellRendererParams: {
				cellClass: [SOFT_BORDER_LEFT]
			},
			headerComponentParams: {
				...simpleNumberDefaultColDef.headerComponentParams,
				headerText: new ColumnText('FUND_ANALYSIS_PORTFOLIOS', 'Number of Portfolios'),
				headerTooltip: new ColumnText('FUND_ANALYSIS_PORTFOLIOS_TOOLTIP')
			},
			comparator: FundAnalysisColumnSortingUtil.defaultColumnComparator
		}]
	}
];

export const aofViewPortfolioColumns: Array<ColDef> = [].concat(...statusViewPortfolioColumns.map(colGroup => colGroup.children));
